import React, { useEffect, useState, useRef } from "react";
import { Button, Col, Container, Spinner } from "react-bootstrap";
import SweetAlert from "react-bootstrap-sweetalert";
import { useHistory } from 'react-router-dom';
import { ProgressBar, getWizardSteps } from 'components/HRInduction/Shared/Helpers';
import { StepOne, StepTwo, StepThree, StepFour, StepFive, StepSix, StepSeven, StepEight, StepNine } from "components/HRInduction/Steps";
import ErrorPage from "components/JobApplication/Shared/ErrorPage";
import { getProgress, toCamelCase } from "components/HRInduction/Shared/Helpers";
import { useQuery } from "components/HRInduction/Shared/Helpers";

function HRInductionWizard(props) {
    const history = useHistory();
    const { inductionId, employeeId, pin, lastCompletedStep } = useQuery();
    const [alert, setAlert] = useState(null);

    const apiURL = process.env.REACT_APP_JOB_URL;
    const [step, setStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [wizardSteps, setWizardSteps] = useState(getWizardSteps());
    const stepsRef = useRef(wizardSteps);
    const [inductionData, setInductionData] = useState({
        rightToWork: {},
        employeePrivacyPolicy: {},
        bankDetails: {},
        previousEmployment: {},
        payslipPassword: {},
        workingTimeRegulations: {},
        keyfob: {},
        nextToKin: {},
        employeeSavingsScheme: {},
        inductionId: inductionId,
        pin: pin,
        employeeId: employeeId,
    });

    useEffect(() => {
        fetchInductionInformation();
    }, []);

    const fetchInductionInformation = async () => {
        try {
            let url = `/HRInduction/GetInduction?inductionId=${inductionId}&employeeId=${employeeId}&pin=${pin}`;
            const response = await fetch(apiURL + url);
            if (response.ok) {
                const body = await response.json();
                if (body) {
                    setInductionData({
                        ...body,
                        inductionId: inductionId,
                        pin: pin,
                        employeeId: employeeId,
                    });
                }
            }
            setStep(1);
            setIsLoading(false);
        } catch (err) {
            setIsLoading(false);
            setAlert(<SweetAlert
                warning
                style={{ display: "block", marginTop: "-100px" }}
                title="Error"
                onConfirm={() => hideAlert()}
                onCancel={() => hideAlert()}
                confirmBtnBsStyle="info"
            >
                Fail to fetch data
            </SweetAlert>);
        }
    };

    const updateStepCompletion = (stepNumber) => {
        stepsRef.current = stepsRef.current.map(step => {
            if (step.number === stepNumber) {
                return { ...step, completed: true };
            } else {
                return step;
            }
        });
    };

    const handleNext = async (data) => {
        try {
            const propertyName = toCamelCase(wizardSteps[step - 1].label);
            await setInductionData((prevState) => ({
                ...prevState,
                [propertyName]: data,
            }));
            updateStepCompletion(step); // Update step completion
            setStep((prevStep) => prevStep + 1); // ADD ONE STEP
        } catch (error) {
            console.error(error);
        }
    };

    const handleBack = async (data) => {
        setStep(step - 1);
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return <StepOne handleNext={handleNext} storedData={inductionData} />;
            case 2:
                return <StepTwo handleNext={handleNext} handleBack={handleBack} storedData={inductionData} />;
            case 3:
                return <StepThree handleNext={handleNext} handleBack={handleBack} storedData={inductionData} />;
            case 4:
                return <StepFour handleNext={handleNext} handleBack={handleBack} storedData={inductionData} />;
            case 5:
                return <StepFive handleNext={handleNext} handleBack={handleBack} storedData={inductionData} />;
            case 6:
                return <StepSix handleNext={handleNext} handleBack={handleBack} storedData={inductionData} />;
            case 7:
                return <StepSeven handleNext={handleNext} handleBack={handleBack} storedData={inductionData} />;
            case 8:
                return <StepEight handleNext={handleNext} handleBack={handleBack} storedData={inductionData} />;
            case 9:
                return <StepNine handleNext={handleNext} handleBack={handleBack} storedData={inductionData} />;
            default:
                return null;
        }
    };

    if (hasError) {
        return <ErrorPage />;
    }

    if (saved) {
        return (
            <Container fluid>
                <div className="saved-container">
                    <h2 className="saved-title">Application Submitted!</h2>
                    <p className="saved-text">
                        Thank you for submitting your application.
                        Our HR team has received your application and will review it in due course.
                        You will be contacted by us soon regarding the status of your application.
                    </p>
                    <Button className="saved-button" href="/FPMCareers">
                        View Other Jobs
                    </Button>
                </div>
            </Container>
        );
    }

    const hideAlert = () => setAlert(null);

    return (
        <Container fluid>
            {alert}
            {isLoading || saving ? (
                <div
                    className="text-center"
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                    }}
                >
                    <Spinner
                        animation="border"
                        style={{
                            width: "200px",
                            height: "200px",
                            border: "9px solid #fff",
                            borderRadius: "50%",
                            borderTopColor: "#1abc9c",
                            fontSize: "60px",
                        }}
                    />
                    <h3>{saving ? 'Submitting Induction, Please Wait...' : isLoading ? 'Loading Induction...' : null}</h3>
                </div>

            ) : (
                <div className="wizard wizard-full-width text-center">
                    <Col className="text-left">
                        <Button
                            className="btn-link"
                            size="lg"
                            variant={"danger"}
                            style={{ margin: "0px", padding: "0px" }}
                            onClick={() => history.push(`/onboarding?employeeId=${employeeId}`)}
                        >
                            <i className="fas fa-chevron-left"></i>
                            Back to onboarding home
                        </Button>
                    </Col>
                    <Col> <img src={require("assets/img/Logo1.png").default} alt="image" /></Col>
                    <h2>Induction for Employee </h2>
                    <ProgressBar progress={getProgress(step)}>   </ProgressBar>

                    <div className="induction-wizard-container text-center">
                        {stepsRef.current.map((step, index) => (
                            <div key={index} className={`item ${step.completed ? 'completed' : ''} ${step.active ? 'active' : ''}`}>
                                <div className="d-flex justify-content-center"><div className="wizard__step-number">{step.completed ? '✓' : index + 1}</div></div>
                                <div className="wizard__step-label">{step.label}</div>
                            </div>))}
                    </div>
                    <div className="wizard__content">
                        {renderStep()}
                    </div>
                </div>
            )}
        </Container>
    );
}

export default HRInductionWizard;
